import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UserDTO, UserFullNameDto, UserNameDto } from '@markant-platform/shared/user/model';
import { BehaviorSubject, catchError, firstValueFrom, map, NEVER } from 'rxjs';
import {
  MappedTableData,
  PaginationPageSize,
  SortAttributes,
  SortDirections,
  TableDraftBuilder,
} from '@markant-platform/shared/table/model';
import { TableComponent, TableService } from '@markant-platform/shared/table/feature';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { AuthorizerService } from '@markant-platform/authorizer/data-access';
import { Page } from '@markant-platform/shared/data-access';
import { AuthorizerErrorCode } from '@markant-platform/shared/error-model';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DEFAULT_SORT_ASSIGNED_EMPLOYEES,
  DEFAULT_SORT_EMPLOYEES,
  TABLE_HEADER_AND_CELLS_ASSIGNED_EMPLOYEES,
  TABLE_HEADER_AND_CELLS_EMPLOYEES,
} from './configuration/group-assignment-member-edit-dialog.config';

@Component({
  selector: 'markant-platform-group-assignment-member-edit-dialog',
  templateUrl: './group-assignment-member-edit-dialog.component.html',
  styleUrls: ['./group-assignment-member-edit-dialog.component.scss'],
})
export class GroupAssignmentMemberEditDialogComponent implements OnInit {
  @Input({ required: true }) groupName = '';
  @Input({ required: true }) groupId = '';

  @Output() updateMembersSuccess = new EventEmitter<void>();
  @Output() dialogClosed = new EventEmitter<void>();

  @ViewChild('employeesListTable') employeesListTable!: TableComponent;

  private readonly LIMITS_REACHED_REGEX = /cartridge variant role limit of (\d+) reached/;

  employeesListTableData!: MappedTableData;
  employeesListTableIsLoading = true;
  preselectedValue!: Map<string, number>;
  searchQuery?: string;
  totalCount = 0;
  pageIndex = 1;
  pageSize: PaginationPageSize = 25;
  sortOrder: 'asc' | 'desc' = 'asc';
  resetSelection$ = new BehaviorSubject<boolean>(false);
  assignedEmployeesTableData!: MappedTableData;
  allEmployees: UserFullNameDto[] = [];
  assignedEmployees: UserFullNameDto[] = [];
  selectedEmployees: UserFullNameDto[] = [];
  assignedEmployeesTableDraft = new TableDraftBuilder(TABLE_HEADER_AND_CELLS_ASSIGNED_EMPLOYEES)
    .tableVariant('accent')
    .disableRowLink(true)
    .sortableColumns(['name'])
    .sort(DEFAULT_SORT_ASSIGNED_EMPLOYEES)
    .actions([
      {
        icon: 'm_close',
        colorScheme: 'dark',
        size: '24',
        function: this.deselectEmployee.bind(this) as (...args: unknown[]) => unknown,
      },
    ])
    .build();
  employeesListTableDraft = new TableDraftBuilder(TABLE_HEADER_AND_CELLS_EMPLOYEES)
    .tableVariant('accent')
    .disableRowLink(true)
    .sortableColumns(['name'])
    .sort(DEFAULT_SORT_EMPLOYEES)
    .checkboxes(true)
    .build();

  private readonly authenticatedUserService = inject(AuthenticatedUserService);
  private readonly userService = inject(UserService);
  private readonly tableService = inject(TableService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly authorizerService = inject(AuthorizerService);
  private readonly destroyRef = inject(DestroyRef);

  async ngOnInit(): Promise<void> {
    await this.loadEmployeesListTableData();

    this.assignedEmployees = this.mapUsersToFullNameDto(
      await firstValueFrom(this.authorizerService.getPagedGroupMembers(this.groupId).pipe(map(page => page.result)))
    );

    this.assignedEmployees.sort((a, b) => a.lastName.localeCompare(b.lastName));
    this.selectedEmployees = [...this.assignedEmployees];
    this.assignedEmployeesTableData = this.tableService.returnMappedTableData(
      this.assignedEmployees,
      this.assignedEmployeesTableDraft
    );
    this.preselectedValue = new Map(this.assignedEmployees.map(dto => [dto.id, 1]));
  }

  private async loadEmployeesListTableData(): Promise<void> {
    this.allEmployees = await this.getAllEmployees();
    this.employeesListTableData = this.tableService.returnMappedTableData(
      this.allEmployees,
      this.employeesListTableDraft
    );
  }

  private async getAllEmployees(): Promise<UserFullNameDto[]> {
    this.employeesListTableIsLoading = true;

    const orgId = await firstValueFrom(this.authenticatedUserService.getOrganizationId());
    const sortAttributes = { sortOrder: this.sortOrder } as SortAttributes;

    const page: Page<UserDTO> = await firstValueFrom(
      this.userService.getUsers(
        orgId,
        undefined,
        {
          index: this.pageIndex - 1,
          size: this.pageSize,
          sort: sortAttributes,
        },
        this.searchQuery,
        undefined,
        true,
        true,
        false
      )
    );

    this.employeesListTableIsLoading = false;
    this.totalCount = page.totalCount;

    return this.mapUsersToFullNameDto(page.result);
  }

  private mapUsersToFullNameDto(users: UserDTO[] | UserNameDto[]): UserFullNameDto[] {
    return users.map(
      user =>
        <UserFullNameDto>{
          id: user.id,
          fullName: user.firstname + ' ' + user.lastname,
          lastName: user.lastname ?? '',
        }
    );
  }

  async onSearchChange(value: string): Promise<void> {
    if (value.length !== 1) {
      this.searchQuery = value;
      await this.loadEmployeesListTableData();
    }
  }

  async onIndexChange(index: number): Promise<void> {
    if (index && this.pageIndex !== index) {
      this.pageIndex = index;
      await this.loadEmployeesListTableData();
    }
  }

  async onPageSizeChange(size: PaginationPageSize): Promise<void> {
    if (size) {
      this.pageIndex = 1;
      this.pageSize = size;
      await this.loadEmployeesListTableData();
    }
  }

  async onSortChange(sort: { columnId: string; sortDirection: SortDirections }): Promise<void> {
    this.sortOrder = sort.sortDirection;

    this.employeesListTableDraft = {
      ...this.employeesListTableDraft,
      sortStartDirection: sort.sortDirection,
    };

    await this.loadEmployeesListTableData();
  }

  assignedEmployeesOnSortChange(sort: { columnId: string; sortDirection: SortDirections }): void {
    this.assignedEmployeesTableDraft = {
      ...this.assignedEmployeesTableDraft,
      sortStartDirection: sort.sortDirection,
    };

    this.selectedEmployees.sort((a, b) =>
      sort.sortDirection === 'asc' ? a.lastName.localeCompare(b.lastName) : b.lastName.localeCompare(a.lastName)
    );

    this.assignedEmployeesTableData = this.tableService.returnMappedTableData(
      this.selectedEmployees,
      this.assignedEmployeesTableDraft
    );
  }

  onToggleAction(selection: Map<string, number>): void {
    // Remove employees who are in employee list table but not selected
    const currentSelection = this.selectedEmployees.filter(
      selectedEmployee =>
        !this.allEmployees.some(listedEmployee => listedEmployee.id === selectedEmployee.id) ||
        selection.has(selectedEmployee.id)
    );

    const newSelection = [...currentSelection];

    // Add new selected employees
    selection.forEach((_, userId) => {
      if (!currentSelection.some(employee => employee.id === userId)) {
        const addedEmployee = this.allEmployees.find(employee => employee.id === userId);

        addedEmployee && newSelection.push(addedEmployee);
      }
    });

    this.selectedEmployees = newSelection;
    this.assignedEmployeesTableData = this.tableService.returnMappedTableData(
      this.selectedEmployees,
      this.assignedEmployeesTableDraft
    );
  }

  deleteAll(): void {
    this.selectedEmployees = [];
    this.assignedEmployeesTableData = this.tableService.returnMappedTableData(
      this.selectedEmployees,
      this.assignedEmployeesTableDraft
    );
    this.resetSelection$.next(true);
    setTimeout(() => this.resetSelection$.next(false));
  }

  deselectEmployee(userId: string): void {
    this.selectedEmployees = this.selectedEmployees.filter(employee => employee.id !== userId);
    this.assignedEmployeesTableData = this.tableService.returnMappedTableData(
      this.selectedEmployees,
      this.assignedEmployeesTableDraft
    );
    this.employeesListTable.forceToggleOff(userId);
  }

  closeDialog(): void {
    this.resetTableConfigurations();
    this.dialogClosed.emit();
  }

  private resetTableConfigurations(): void {
    this.pageIndex = 1;
    this.pageSize = 25;
    this.sortOrder = 'asc';
    this.searchQuery = undefined;
  }

  updateMembers(): void {
    if (this.selectedEmployees.length === 0) {
      this.snackbarService.showErrorSnackbarWithTransloco(
        'dashboardOverview.groupAssignment.memberEditDialog.noUserSelected'
      );
      return;
    }

    const selectedIds = this.selectedEmployees.map(employee => employee.id);
    const assignedIds = this.assignedEmployees.map(member => member.id);

    const usersToAdd = selectedIds.filter(id => !assignedIds.includes(id));
    const usersToRemove = assignedIds.filter(id => !selectedIds.includes(id));

    if (usersToAdd.length || usersToRemove.length) {
      this.authorizerService
        .changeGroupsUsers(usersToAdd, usersToRemove, this.groupId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(err => {
            this.handleChangeGroupsUsersError(err);
            return NEVER;
          })
        )
        .subscribe(() => {
          this.updateMembersSuccess.emit();
          this.snackbarService.showSuccessSnackbarWithTransloco(
            'dashboardOverview.groupAssignment.detail.editMember.success'
          );
          this.closeDialog();
        });
    }
  }

  private handleChangeGroupsUsersError(err: HttpErrorResponse): void {
    if (err.error?.errorCode === AuthorizerErrorCode.AUTHORIZER_ADD_USERS_TO_GROUPS_LIMIT_REACHED) {
      const limitMatch = err?.error?.message.match(this.LIMITS_REACHED_REGEX);
      const limit = limitMatch ? limitMatch[1] : 'unknown';
      this.snackbarService.showErrorSnackbarWithTransloco(
        'dashboardOverview.groupAssignment.detail.editMember.errors.AUT-040',
        { limit }
      );
    } else {
      this.snackbarService.showErrorSnackbarWithTransloco(
        'dashboardOverview.groupAssignment.detail.editMember.errors.default'
      );
    }

    this.selectedEmployees = [...this.assignedEmployees];
    this.preselectedValue = new Map(this.assignedEmployees.map(dto => [dto.id, 1]));
    this.assignedEmployeesTableData = this.tableService.returnMappedTableData(
      this.selectedEmployees,
      this.assignedEmployeesTableDraft
    );
  }
}
